/**
 * Sincroniza pausa do Escritório Virtual quando o usuário entra em outra reunião de vídeo.
 */

const CHANNEL_NAME = "xpro-virtual-office-session";
const STORAGE_KEY = "xpro.virtualOffice.session";

let sharedChannel = null;

function ensureChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (sharedChannel) return sharedChannel;
  try {
    sharedChannel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    sharedChannel = null;
  }
  return sharedChannel;
}

function storage() {
  // localStorage: sessão compartilhada entre aba do cockpit e aba do vídeo.
  if (typeof localStorage !== "undefined") return localStorage;
  if (typeof sessionStorage !== "undefined") return sessionStorage;
  return null;
}

export function readVirtualOfficeSession() {
  const store = storage();
  if (!store) return null;
  try {
    let raw = store.getItem(STORAGE_KEY);
    // Migra sessão antiga (sessionStorage) para localStorage (cross-tab).
    if (!raw && typeof sessionStorage !== "undefined" && store !== sessionStorage) {
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        try {
          store.setItem(STORAGE_KEY, raw);
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
    }
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeVirtualOfficeSession(session) {
  const store = storage();
  if (!store) return;
  try {
    if (!session) {
      store.removeItem(STORAGE_KEY);
    } else {
      store.setItem(STORAGE_KEY, JSON.stringify(session));
    }
  } catch {
    /* ignore */
  }
  const ch = ensureChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "session", session, at: Date.now() });
  } catch {
    /* ignore */
  }
}

export function publishOtherMeetingStarted({ title, ref = null } = {}) {
  const ch = ensureChannel();
  const payload = {
    type: "other_meeting_started",
    title: title || "Outra reunião",
    ref,
    at: Date.now(),
  };
  if (ch) {
    try {
      ch.postMessage(payload);
    } catch {
      /* ignore */
    }
  }
  const current = readVirtualOfficeSession();
  if (current) {
    writeVirtualOfficeSession({
      ...current,
      status: "in_other_meeting",
      otherMeetingTitle: payload.title,
      otherMeetingRef: ref,
    });
  }
  return payload;
}

export function publishOtherMeetingEnded() {
  const ch = ensureChannel();
  const payload = { type: "other_meeting_ended", at: Date.now() };
  if (ch) {
    try {
      ch.postMessage(payload);
    } catch {
      /* ignore */
    }
  }
  const current = readVirtualOfficeSession();
  if (current) {
    writeVirtualOfficeSession({
      ...current,
      status: "present",
      otherMeetingTitle: null,
      otherMeetingRef: null,
    });
  }
  return payload;
}

export function publishVirtualOfficePauseTracks(payload = {}) {
  const ch = ensureChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "pause_tracks", ...payload, at: Date.now() });
  } catch {
    /* ignore */
  }
}

export function publishVirtualOfficeResumeTracks(payload = {}) {
  const ch = ensureChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "resume_tracks", ...payload, at: Date.now() });
  } catch {
    /* ignore */
  }
}

export function subscribeVirtualOfficeBridge(handler) {
  if (typeof handler !== "function") return () => {};
  const ch = ensureChannel();
  const onMessage = (ev) => {
    try {
      handler(ev?.data || null);
    } catch {
      /* ignore */
    }
  };
  const onStorage = (ev) => {
    if (!ev || ev.key !== STORAGE_KEY) return;
    try {
      const session = ev.newValue ? JSON.parse(ev.newValue) : null;
      handler({ type: "session", session, at: Date.now() });
    } catch {
      handler({ type: "session", session: null, at: Date.now() });
    }
  };
  if (ch) ch.addEventListener("message", onMessage);
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    if (ch) ch.removeEventListener("message", onMessage);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}
